import { Button } from '@trustoo/components/Button/v2'
import { Input } from '@trustoo/components/input/v2'
import { Select, type SelectOption } from '@trustoo/components/Select/v2'
import { Tag } from '@trustoo/components/Tag/v2'
import React from 'react'
import { CodeSnippet, InlineCode, MIcon, Section } from './shared'

// ---------- Input (v2) showcase ----------
// Generated from the Figma "Input field" (5:11773) ComponentSet by the
// component-sync pipeline. Shown across the prop-driven states; Hover / Focused
// are native pseudo-classes on the real element, so the "focused" cells force
// the visual via the `state` prop.

const INPUT_SIZES: ('sm' | 'md')[] = ['sm', 'md']

const INPUT_USAGE = `import { Input } from '@trustoo/components/input/v2'

// Basic (uncontrolled; pass value + onChange to control it)
<Input label="Email" placeholder="you@example.com" onChange={onChange} />

// Type variants — variant selects the adornment region
<Input variant="leading-text" leadingText="http://" label="Website" />
// Leading / trailing dropdowns take the real Select in addon mode (unstyled):
// it shares the Input's field shell with no double border.
<Input variant="leading-dropdown" label="Phone"
    leadingAddon={<Select unstyled defaultValue="us" options={countryCodes} aria-label="Country code" />} />
<Input variant="trailing-dropdown" label="Amount"
    trailingAddon={<Select unstyled defaultValue="usd" options={currencies} aria-label="Currency" />} />
<Input variant="payment-input" iconLeading={<CardIcon />} label="Card number" />
<Input variant="tags" label="Users" tags={users.map((u) => (
    <Tag key={u.id} size="sm" onClose={() => remove(u.id)}>{u.name}</Tag>
))} />
<Input variant="trailing-button" trailingButton={<Button size="sm">Button CTA</Button>} label="Website" />

// Validation / destructive
<Input label="Email" error="Please enter a valid email address." />
<Input label="Email" destructive />`

// Renders a Material Symbols glyph (the font the rest of the app already loads).
// The Input leading / trailing dropdown slots take the real Select in addon
// mode (unstyled): it drops its own border/height/chrome and shares the Input's
// field shell, matching Figma's leading-dropdown / trailing-dropdown variants.
const INPUT_COUNTRY_CODES: SelectOption[] = [
    { value: 'us', label: 'US' },
    { value: 'nl', label: 'NL' },
    { value: 'de', label: 'DE' },
    { value: 'gb', label: 'GB' },
    { value: 'es', label: 'ES' },
]

const INPUT_CURRENCIES: SelectOption[] = [
    { value: 'usd', label: 'USD' },
    { value: 'eur', label: 'EUR' },
    { value: 'gbp', label: 'GBP' },
]

const InputSizeBlock: React.FC<{ size: 'sm' | 'md' }> = ({ size }) => (
    <div className="space-y-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
        <div className="flex items-baseline gap-spacing-md">
            <span className="text-sm font-semibold text-text-primary">
                {size === 'sm' ? 'Small' : 'Medium'}
            </span>
            <InlineCode>size=&quot;{size}&quot;</InlineCode>
        </div>
        <Input size={size} label="Email" placeholder="you@example.com" hintText="Placeholder" />
        <Input size={size} label="Email" value="olivia@untitledui.com" hintText="Filled" />
        <Input
            size={size}
            label="Email"
            placeholder="you@example.com"
            state="focused"
            hintText="Focused (forced)"
        />
        <Input
            size={size}
            label="Email"
            placeholder="you@example.com"
            disabled
            hintText="Disabled"
        />
        <Input
            size={size}
            label="Search"
            placeholder="Search"
            iconLeading={<MIcon name="search" className="text-lg text-text-tertiary" />}
            hintText="Leading icon"
        />
        <Input
            size={size}
            label="Email"
            value="not-an-email"
            error="Please enter a valid email address."
        />
        <Input
            size={size}
            label="Email"
            placeholder="you@example.com"
            destructive
            hintText="Destructive"
        />
    </div>
)

const InputVariantsBlock: React.FC = () => (
    <div className="space-y-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
        <Input
            variant="default"
            label="Email"
            placeholder="you@example.com"
            iconLeading={<MIcon name="mail" className="text-lg text-text-tertiary" />}
            hintText="default + leading icon"
        />
        <Input
            variant="leading-dropdown"
            label="Phone number"
            placeholder="+1 (555) 000-0000"
            leadingAddon={
                <Select
                    unstyled
                    defaultValue="us"
                    options={INPUT_COUNTRY_CODES}
                    aria-label="Country code"
                />
            }
            hintText="leading-dropdown"
        />
        <Input
            variant="leading-text"
            label="Website"
            placeholder="www.untitledui.com"
            leadingText="http://"
            hintText="leading-text"
        />
        <Input
            variant="trailing-dropdown"
            label="Sale amount"
            placeholder="1,000.00"
            trailingAddon={
                <Select
                    unstyled
                    defaultValue="usd"
                    options={INPUT_CURRENCIES}
                    aria-label="Currency"
                />
            }
            hintText="trailing-dropdown"
        />
        <Input
            variant="payment-input"
            label="Card number"
            placeholder="1234 1234 1234 1234"
            iconLeading={<MIcon name="credit_card" className="text-lg text-text-tertiary" />}
            hintText="payment-input"
        />
        <Input
            variant="tags"
            label="Users"
            placeholder="Add users"
            tags={
                <>
                    <Tag size="sm" onClose={() => undefined}>
                        Olivia
                    </Tag>
                    <Tag size="sm" onClose={() => undefined}>
                        Phoenix
                    </Tag>
                    <Tag size="sm" onClose={() => undefined}>
                        Lana
                    </Tag>
                </>
            }
            hintText="tags (real Tag component)"
        />
        <Input
            variant="trailing-button"
            label="Website"
            value="www.untitledui.com"
            trailingButton={<Button size="sm">Button CTA</Button>}
            hintText="trailing-button (uses the Button component)"
        />
    </div>
)

export const InputShowcase: React.FC = () => (
    <Section
        title="Input (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Input field</span> set
                (ComponentSet <InlineCode>5:11773</InlineCode>). Import v2 explicitly; the{' '}
                <InlineCode>@trustoo/components/input</InlineCode> barrel still resolves to{' '}
                <InlineCode>v1</InlineCode> (the legacy <InlineCode>InputField</InlineCode>) until
                v2 is promoted.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <p className="text-xs text-text-tertiary">
                Each column is a <span className="font-semibold">size</span>; the rows walk the
                prop-driven states (placeholder, filled, focused, disabled), an example with a{' '}
                <span className="font-semibold">leading icon</span>, a{' '}
                <span className="font-semibold">validation error</span>, and the{' '}
                <span className="font-semibold">destructive</span> palette. Tab into any field to
                see the live focus ring.
            </p>

            <div className="grid gap-spacing-xl sm:grid-cols-2">
                {INPUT_SIZES.map((size) => (
                    <InputSizeBlock key={size} size={size} />
                ))}
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    Type variants
                </h3>
                <p className="mb-spacing-lg text-xs text-text-tertiary">
                    Each <span className="font-semibold">Type</span> from Figma renders a distinct
                    adornment: a leading <span className="font-semibold">icon</span>, a leading /
                    trailing <span className="font-semibold">dropdown</span>, a leading{' '}
                    <span className="font-semibold">text prefix</span>, a{' '}
                    <span className="font-semibold">payment</span> card icon,{' '}
                    <span className="font-semibold">tag</span> chips, and a trailing{' '}
                    <span className="font-semibold">button</span>. The selects, chips and button
                    below are demo stand-ins — pass your own nodes into the matching slot (e.g. the{' '}
                    <InlineCode>Select</InlineCode> component below for the dropdown slots).
                </p>
                <InputVariantsBlock />
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={INPUT_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>size</InlineCode> (sm · md), <InlineCode>variant</InlineCode>{' '}
                    (default · leading-dropdown · trailing-dropdown · leading-text · payment-input ·
                    tags · trailing-button), <InlineCode>state</InlineCode> (placeholder · filled ·
                    focused · disabled), <InlineCode>destructive</InlineCode>,{' '}
                    <InlineCode>label</InlineCode>, <InlineCode>hintText</InlineCode>,{' '}
                    <InlineCode>required</InlineCode>, <InlineCode>helpIcon</InlineCode>, the slot
                    props <InlineCode>iconLeading</InlineCode> / <InlineCode>iconSwap</InlineCode> /{' '}
                    <InlineCode>leadingText</InlineCode> / <InlineCode>leadingAddon</InlineCode> /{' '}
                    <InlineCode>trailingAddon</InlineCode> / <InlineCode>trailingButton</InlineCode>{' '}
                    / <InlineCode>tags</InlineCode>, plus <InlineCode>error</InlineCode> and{' '}
                    <InlineCode>withErrorIcon</InlineCode>. All native{' '}
                    <InlineCode>&lt;input&gt;</InlineCode> props pass through and the component
                    forwards its ref.
                </p>
            </div>
        </div>
    </Section>
)
